"use client"

import { useState } from "react"
import { ChevronRight } from 'lucide-react'
import { useLocation, useNavigate } from "react-router-dom";

interface CurriculumContent {
  id: number
  name: string
  status: "completed" | "in-progress" | "upcoming" | "locked"
}

interface Subject {
  id: number
  name: string
  icon?: string
  content: CurriculumContent[]
}

interface GradeClass {
  id: number
  name: string
  subjects: Subject[]
}

interface CurriculumTrackerProps {
  classes?: GradeClass[]
}

export function CurriculumTracker({ classes }: CurriculumTrackerProps) {
  const [expandedClasses, setExpandedClasses] = useState<number[]>([])
  const [expandedSubjects, setExpandedSubjects] = useState<string[]>([])
  const navigate = useNavigate();

  const defaultClasses: GradeClass[] = [
    {
      id: 1,
      name: "JSS 1",
      subjects: [
        {
          id: 1,
          name: "English Language",
          icon: "",
          content: [
            { id: 1, name: "Grammar Basics", status: "completed" },
            { id: 2, name: "Reading Comprehension", status: "completed" },
            { id: 3, name: "Writing Skills", status: "in-progress" },
            { id: 4, name: "Vocabulary Building", status: "upcoming" },
            { id: 5, name: "Literature Analysis", status: "locked" },
          ],
        },
        {
          id: 2,
          name: "Mathematics",
          icon: "",
          content: [
            { id: 6, name: "Number Theory", status: "completed" },
            { id: 7, name: "Algebra Fundamentals", status: "in-progress" },
            { id: 8, name: "Geometry Basics", status: "upcoming" },
            { id: 9, name: "Statistics", status: "locked" },
          ],
        },
        {
          id: 3,
          name: "Science",
          icon: "",
          content: [
            { id: 10, name: "Biology Basics", status: "completed" },
            { id: 11, name: "Physics Principles", status: "in-progress" },
            { id: 12, name: "Chemistry Intro", status: "upcoming" },
          ],
        },
      ],
    },
    {
      id: 2,
      name: "JSS 2",
      subjects: [
        {
          id: 4,
          name: "English Language",
          icon: "",
          content: [
            { id: 13, name: "Advanced Grammar", status: "completed" },
            { id: 14, name: "Essay Writing", status: "in-progress" },
            { id: 15, name: "Poetry Analysis", status: "upcoming" },
          ],
        },
        {
          id: 5,
          name: "Mathematics",
          icon: "",
          content: [
            { id: 16, name: "Quadratic Equations", status: "completed" },
            { id: 17, name: "Trigonometry", status: "in-progress" },
            { id: 18, name: "Calculus Intro", status: "locked" },
          ],
        },
        {
          id: 6,
          name: "History",
          icon: "",
          content: [
            { id: 19, name: "Ancient Civilizations", status: "completed" },
            { id: 20, name: "Medieval History", status: "in-progress" },
          ],
        },
      ],
    },
    {
      id: 3,
      name: "JSS 3",
      subjects: [
        {
          id: 7,
          name: "English Language",
          icon: "",
          content: [
            { id: 21, name: "Shakespearean Literature", status: "in-progress" },
            { id: 22, name: "Technical Writing", status: "upcoming" },
          ],
        },
        {
          id: 8,
          name: "Mathematics",
          icon: "",
          content: [
            { id: 23, name: "Advanced Algebra", status: "in-progress" },
            { id: 24, name: "Statistics & Probability", status: "upcoming" },
          ],
        },
      ],
    },
    {
      id: 4,
      name: "SS 1",
      subjects: [
        {
          id: 9,
          name: "Further Mathematics",
          icon: "",
          content: [
            { id: 25, name: "Complex Numbers", status: "upcoming" },
            { id: 26, name: "Series & Sequences", status: "locked" },
          ],
        },
        {
          id: 10,
          name: "Economics",
          icon: "",
          content: [
            { id: 27, name: "Microeconomics", status: "upcoming" },
            { id: 28, name: "Macroeconomics", status: "locked" },
          ],
        },
      ],
    },
    {
      id: 5,
      name: "SS 2",
      subjects: [
        {
          id: 11,
          name: "Literature in English",
          icon: "",
          content: [
            { id: 29, name: "Prose Analysis", status: "upcoming" },
            { id: 30, name: "Drama Studies", status: "locked" },
          ],
        },
        {
          id: 12,
          name: "Government",
          icon: "",
          content: [
            { id: 31, name: "Political Systems", status: "upcoming" },
            { id: 32, name: "Constitutional Law", status: "locked" },
          ],
        },
      ],
    },
    {
      id: 6,
      name: "SS 3",
      subjects: [
        {
          id: 13,
          name: "Physics",
          icon: "",
          content: [
            { id: 33, name: "Mechanics", status: "locked" },
            { id: 34, name: "Waves & Optics", status: "locked" },
          ],
        },
        {
          id: 14,
          name: "Chemistry",
          icon: "",
          content: [
            { id: 35, name: "Organic Chemistry", status: "locked" },
            { id: 36, name: "Inorganic Chemistry", status: "locked" },
          ],
        },
      ],
    },
  ]

  const gradeClasses = classes || defaultClasses

  const toggleClass = (classId: number) => {
    setExpandedClasses((prev) =>
      prev.includes(classId) ? prev.filter((id) => id !== classId) : [...prev, classId]
    )
    console.log("Toggled class", classId)
  }

  const toggleSubject = (subjectId: string) => {
    setExpandedSubjects((prev) =>
      prev.includes(subjectId) ? prev.filter((id) => id !== subjectId) : [...prev, subjectId]
    )
  }

  const handleTopicClick = (topic: CurriculumContent) => {
    if (topic.status !== "locked") {
      navigate('/curriculum')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-50"
      case "in-progress":
        return "text-blue-600 bg-blue-50"
      case "upcoming":
        return "text-amber-600 bg-amber-50"
      case "locked":
        return "text-slate-400 bg-slate-50"
      default:
        return ""
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-200 text-green-700"
      case "in-progress":
        return "bg-blue-200 text-blue-700"
      case "upcoming":
        return "bg-amber-200 text-amber-700"
      case "locked":
        return "bg-slate-200 text-slate-600"
      default:
        return ""
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return "✓"
      case "in-progress":
        return "•"
      case "upcoming":
        return "◷"
      case "locked":
        return "🔒"
      default:
        return ""
    }
  }

  return (
    <div className="bg-slate-100 p-6 rounded-2xl border border-slate-200">
      <div className="flex items-centfer gap-4 mb-6">
        <div className="p-3 bg-blue-100 rounded-full">
          <span className="text-blue-600 text-xl"></span>
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Curriculum Tracker</h2>
      </div>

      {/* Grade Class Nodes */}
      <div className="space-y-1">
        {gradeClasses.map((gradeClass, classIndex) => {
          const isClassExpanded = expandedClasses.includes(gradeClass.id)
          const isLastClass = classIndex === gradeClasses.length - 1

          return (
            <div key={gradeClass.id}>
              {/* Grade Class Node */}
              <button
                onClick={() => toggleClass(gradeClass.id)}
                className="w-full flex items-center gap-2 px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors text-left group"
              >
                <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                  <ChevronRight
                    className={`w-5 h-5 text-slate-400 transition-transform ${
                      isClassExpanded ? "rotate-90" : ""
                    }`}
                  />
                </div>
                <span className="font-bold text-slate-900 min-w-fit">{gradeClass.name}</span>
                <span className="ml-auto bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                  {gradeClass.subjects.length}
                </span>
              </button>

              {/* Subjects Tree */}
              {isClassExpanded && (
                <div className="ml-6 space-y-1 border-l-2 border-slate-200 pl-0">
                  {gradeClass.subjects.map((subject, subjectIndex) => {
                    const subjectKey = `${gradeClass.id}-${subject.id}`
                    const isSubjectExpanded = expandedSubjects.includes(subjectKey)
                    const isLastSubject = subjectIndex === gradeClass.subjects.length - 1

                    return (
                      <div key={subject.id}>
                        {/* Subject Node */}
                        <button
                          onClick={() => toggleSubject(subjectKey)}
                          className="w-full flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                        >
                          <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                            <ChevronRight
                              className={`w-4 h-4 text-slate-400 transition-transform ${
                                isSubjectExpanded ? "rotate-90" : ""
                              }`}
                            />
                          </div>
                          {subject.icon && <span className="text-base">{subject.icon}</span>}
                          <span className="font-semibold text-slate-800 text-sm">{subject.name}</span>
                          <span className="ml-auto bg-slate-100 text-slate-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                            {subject.content.length}
                          </span>
                        </button>

                        {/* Topics Tree */}
                        {isSubjectExpanded && (
                          <div className="ml-6 space-y-0.5 border-l-2 border-slate-200 pl-0">
                            {subject.content.map((topic, topicIndex) => {
                              const isLastTopic = topicIndex === subject.content.length - 1

                              return (
                                <button
                                  key={topic.id}
                                  onClick={() => handleTopicClick(topic)}
                                  disabled={topic.status === "locked"}
                                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-all group ${
                                    topic.status === "locked"
                                      ? "cursor-not-allowed opacity-60"
                                      : "cursor-pointer hover:bg-slate-50"
                                  }`}
                                >
                                  {/* Tree connector dot */}
                                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                                    <div className={`w-2 h-2 rounded-full ${getStatusColor(topic.status).split(" ")[0].replace("text-", "bg-")}`}></div>
                                  </div>
                                  
                                  <span className={`text-sm font-medium flex-grow ${getStatusColor(topic.status).split(" ")[0]}`}>
                                    {topic.name}
                                  </span>

                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getStatusBadgeColor(topic.status)}`}>
                                      {getStatusIcon(topic.status)}
                                    </span>
                                  </div>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-slate-200 text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-600 rounded-full"></div>
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
          <span>Upcoming</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
          <span>Locked</span>
        </div>
      </div>
    </div>
  )
}
